/* =====================================================================
 * DMSettingsModal — DM 설정 모달 (알림, 스케줄 등)
 * ===================================================================== */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Modal } from '@auw/workspace-ui';
import type { NotificationSettings } from '@auw/workspace-utils/types/dm';
import { useDMStore } from './hooks/useDMStore';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  layerZIndex?: number;
  onActivate?: () => void;
}

const DMSettingsModal: React.FC<Props> = ({ isOpen, onClose, layerZIndex, onActivate }) => {
  const { t } = useTranslation('common');
  const { settings, loadSettings, updateSettings } = useDMStore();
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    if (isOpen && !settings) {
      loadSettings();
    }
  }, [isOpen, settings, loadSettings]);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const notifSettings = localSettings?.notification_settings;

  const toggleAgent = (key: keyof NotificationSettings, enabled: boolean) => {
    if (!notifSettings) return;
    const updated = {
      ...notifSettings,
      [key]: { ...notifSettings[key], enabled },
    };
    updateSettings({ notification_settings: updated });
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={t('dm.settings')}
      width="480px"
      layerZIndex={layerZIndex}
      onActivate={onActivate}
    >
      <ModalBody>
        <Section>
          <SectionTitle>{t('dm.notificationSettings')}</SectionTitle>
          <SectionDesc>{t('dm.notificationDesc')}</SectionDesc>

          <SettingRow>
            <SettingInfo>
              <SettingAvatar>🤖</SettingAvatar>
              <div>
                <SettingName>askyour.ai</SettingName>
                <SettingDesc>{t('dm.systemNotifications')}</SettingDesc>
              </div>
            </SettingInfo>
            <Toggle
              $active={notifSettings?.system_app?.enabled ?? true}
              onClick={() => toggleAgent('system_app', !(notifSettings?.system_app?.enabled ?? true))}
              role="switch"
              aria-checked={notifSettings?.system_app?.enabled ?? true}
            >
              <ToggleKnob $active={notifSettings?.system_app?.enabled ?? true} />
            </Toggle>
          </SettingRow>
        </Section>

        <Section>
          <SectionTitle>{t('dm.languageAndTimezone')}</SectionTitle>
          <InfoRow>
            <InfoLabel>{t('dm.language')}</InfoLabel>
            <InfoValue>{localSettings?.preferred_language ?? 'ko'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t('dm.timezone')}</InfoLabel>
            <InfoValue>{localSettings?.timezone ?? 'Asia/Seoul'}</InfoValue>
          </InfoRow>
        </Section>
      </ModalBody>
    </Modal>
  );
};

export default DMSettingsModal;

/* ---- Styled ---- */

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const SectionDesc = styled.p`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 0;
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const SettingInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SettingAvatar = styled.span`
  font-size: 1.25rem;
`;

const SettingName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SettingDesc = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Toggle = styled.div<{ $active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  padding: 2px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.success : theme.colors.bgTertiary};
`;

const ToggleKnob = styled.div<{ $active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
  transform: translateX(${({ $active }) => ($active ? '20px' : '0')});
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InfoValue = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
`;
